/**
 * Mock Budget Repository
 *
 * 개발/테스트용 Mock Budget 데이터 저장소
 * mockBudgets 배열을 사용하여 CRUD 작업 시뮬레이션
 */

import { Budget } from '../types/Budget'
import { mockBudgets } from '../mocks/mockBudgets'
import { BudgetRepository, BudgetFilter } from './BudgetRepository'

/**
 * Mock Budget Repository 구현
 *
 * 책임:
 * - mockBudgets 배열을 데이터 소스로 사용
 * - 필터링, CRUD 작업 시뮬레이션
 * - 비동기 작업 시뮬레이션 (실제 API와 유사한 동작)
 */
export class MockBudgetRepository implements BudgetRepository {
  /**
   * In-memory budget 저장소
   * 런타임 중 CRUD 작업 결과를 유지
   */
  private budgets: Budget[] = [...mockBudgets]

  /**
   * 필터 조건에 맞는 예산 목록 조회
   *
   * @param filter 조회 필터
   * @returns 필터링된 예산 배열
   *
   * 필터 조건:
   * - year/month: 템플릿 포함 + 우선순위 로직 적용
   *   - 특정 year + 특정 month (최우선)
   *   - 특정 year + month: null (해당 연도 템플릿)
   *   - year: null + month: null (완전 템플릿, 모든 년도/월)
   * - categoryId: 카테고리 ID 일치
   * - isActive: 활성 상태 일치
   */
  async findAll(filter?: BudgetFilter): Promise<Budget[]> {
    // 비동기 작업 시뮬레이션 (50ms 지연)
    await this.delay(50)

    let result = [...this.budgets]

    /**
     * year/month 필터링 시 템플릿 포함 + 우선순위 처리
     *
     * 로직:
     * 1. 해당하는 모든 예산 조회 (특정 값 + 템플릿)
     * 2. categoryId별로 가장 구체적인 예산만 선택
     */
    if (filter && (filter.year !== undefined || filter.month !== undefined)) {
      const targetYear = filter.year
      const targetMonth = filter.month

      // 1단계: 해당하는 모든 예산 조회 (템플릿 포함)
      result = result.filter(b => {
        // year 체크: 특정 year 또는 템플릿(null)
        const matchYear = targetYear !== undefined
          ? (b.year === targetYear || b.year === null)
          : true

        // month 체크: 특정 month 또는 템플릿(null)
        const matchMonth = targetMonth !== undefined
          ? (b.month === targetMonth || b.month === null)
          : true

        return matchYear && matchMonth
      })

      // 2단계: categoryId별로 우선순위가 가장 높은 예산만 선택
      const categoryBudgets = new Map<string, Budget>()

      for (const budget of result) {
        const categoryId = budget.categoryId
        const existing = categoryBudgets.get(categoryId)

        if (!existing) {
          categoryBudgets.set(categoryId, budget)
          continue
        }

        // 우선순위 비교: 더 높은 우선순위만 유지
        const currentPriority = this.getBudgetPriority(budget, targetYear, targetMonth)
        const existingPriority = this.getBudgetPriority(existing, targetYear, targetMonth)

        if (currentPriority > existingPriority) {
          categoryBudgets.set(categoryId, budget)
        }
      }

      result = Array.from(categoryBudgets.values())
    }

    // 기타 필터 적용
    if (filter?.categoryId) {
      result = result.filter(b => b.categoryId === filter.categoryId)
    }

    if (filter?.parentBudgetId !== undefined) {
      result = result.filter(b => b.parentBudgetId === filter.parentBudgetId)
    }

    if (filter?.isActive !== undefined) {
      result = result.filter(b => b.isActive === filter.isActive)
    }

    // Deep copy로 불변성 보장
    return result.map(b => ({ ...b }))
  }

  /**
   * ID로 특정 예산 조회
   *
   * @param id 예산 ID
   * @returns 예산 또는 null
   */
  async findById(id: string): Promise<Budget | null> {
    await this.delay(50)

    const budget = this.budgets.find(b => b.id === id)
    return budget ? { ...budget } : null
  }

  /**
   * 새 예산 생성
   *
   * @param input 예산 입력 데이터 (id 제외)
   * @returns 생성된 예산
   */
  async create(input: Omit<Budget, 'id'>): Promise<Budget> {
    await this.delay(50)

    // 새 ID 생성 (budget_XXX 형식)
    const newId = this.generateId()

    const newBudget: Budget = {
      id: newId,
      ...input,
    }

    this.budgets.push(newBudget)

    return { ...newBudget }
  }

  /**
   * 예산 수정
   *
   * @param id 예산 ID
   * @param input 수정할 필드
   * @returns 수정된 예산
   * @throws 예산을 찾을 수 없는 경우
   */
  async update(id: string, input: Partial<Budget>): Promise<Budget> {
    await this.delay(50)

    const index = this.budgets.findIndex(b => b.id === id)
    if (index === -1) {
      throw new Error(`Budget not found: ${id}`)
    }

    // 기존 데이터와 병합
    // 타입 단언 사용: this.budgets[index]가 완전한 Budget이므로 병합 결과도 Budget
    const updated = {
      ...this.budgets[index],
      ...input,
      id, // ID는 변경 불가
    } as Budget

    this.budgets[index] = updated

    return { ...updated }
  }

  /**
   * 예산 삭제
   *
   * @param id 예산 ID
   * @throws 예산을 찾을 수 없는 경우
   */
  async delete(id: string): Promise<void> {
    await this.delay(50)

    const index = this.budgets.findIndex(b => b.id === id)
    if (index === -1) {
      throw new Error(`Budget not found: ${id}`)
    }

    this.budgets.splice(index, 1)
  }

  /**
   * parentBudgetId로 예산 조회 (cascade 작업용)
   * @param parentBudgetId 부모 예산 ID
   * @returns 자식 예산 배열 (활성화된 것만)
   */
  async findByParentBudgetId(parentBudgetId: string): Promise<Budget[]> {
    return this.findAll({ parentBudgetId, isActive: true })
  }

  /**
   * 비동기 작업 시뮬레이션용 지연 함수
   *
   * @param ms 지연 시간 (밀리초)
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * 예산의 우선순위 계산
   *
   * 우선순위 (높을수록 우선):
   * - 3: 특정 year + 특정 month (최우선)
   * - 2: 특정 year + month: null (해당 연도 템플릿)
   * - 1: year: null + month: null (완전 템플릿)
   * - 0: 매칭 안 됨
   *
   * @param budget 예산 객체
   * @param targetYear 조회할 year
   * @param targetMonth 조회할 month
   * @returns 우선순위 점수 (0-3)
   */
  private getBudgetPriority(
    budget: Budget,
    targetYear?: number | null,
    targetMonth?: number | null
  ): number {
    // 특정 year + 특정 month = 3 (최우선)
    if (budget.year === targetYear && budget.month === targetMonth) {
      return 3
    }

    // 특정 year + month: null = 2 (해당 연도 템플릿)
    if (budget.year === targetYear && budget.month === null) {
      return 2
    }

    // year: null + month: null = 1 (완전 템플릿)
    if (budget.year === null && budget.month === null) {
      return 1
    }

    // 매칭 안 됨
    return 0
  }

  /**
   * 새 Budget ID 생성
   *
   * @returns budget_XXX 형식의 ID
   */
  private generateId(): string {
    const maxId = this.budgets
      .map(b => parseInt(b.id.replace('budget_', '')))
      .reduce((max, current) => Math.max(max, current), 0)

    const newIdNumber = maxId + 1
    return `budget_${String(newIdNumber).padStart(3, '0')}`
  }
}
